/**
 * Pure Canvas2D drawing module — no React dependency.
 * All rendering logic lives here; components only call these functions.
 */

const MASK_OVERLAY_COLOR = 'rgba(255, 0, 0, 0.5)';

/** Draw an image onto a canvas context, filling the entire canvas. */
export function drawImage(ctx: CanvasRenderingContext2D, image: HTMLImageElement): void {
  ctx.drawImage(
    image,
    0,
    0,
    ctx.canvas.width / getDevicePixelRatio(),
    ctx.canvas.height / getDevicePixelRatio(),
  );
}

/**
 * Draw a brush stroke point on the mask canvas.
 * - brush mode: paints white pixels (source-over)
 * - eraser mode: clears pixels (destination-out)
 */
export function drawBrushStroke(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  tool: 'brush' | 'eraser',
  pressure?: number,
): void {
  ctx.save();

  if (tool === 'brush') {
    ctx.globalCompositeOperation = 'source-over';
    const opacity = pressure !== undefined && pressure > 0 ? Math.max(0.2, pressure) : 1.0;
    ctx.globalAlpha = opacity;
    ctx.fillStyle = '#ffffff';
  } else {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = 1.0;
    ctx.fillStyle = '#000000';
  }

  ctx.beginPath();
  ctx.arc(x, y, size / 2, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

/** Draw a cursor preview circle on the UI layer. */
export function drawCursorPreview(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  tool: 'brush' | 'eraser',
): void {
  ctx.save();
  ctx.strokeStyle = tool === 'brush' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 100, 100, 0.8)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x, y, size / 2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Clear the entire cursor layer. */
export function clearCursorLayer(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(
    0,
    0,
    ctx.canvas.width / getDevicePixelRatio(),
    ctx.canvas.height / getDevicePixelRatio(),
  );
}

/**
 * Composite the binary mask onto the overlay canvas with a colored tint.
 * Reads from the mask canvas and draws colored overlay for user visibility.
 */
export function compositeMaskOverlay(
  maskCtx: CanvasRenderingContext2D,
  overlayCtx: CanvasRenderingContext2D,
): void {
  const w = overlayCtx.canvas.width / getDevicePixelRatio();
  const h = overlayCtx.canvas.height / getDevicePixelRatio();

  // Clear overlay
  overlayCtx.clearRect(0, 0, w, h);

  // Draw the mask canvas onto the overlay
  overlayCtx.save();
  overlayCtx.drawImage(maskCtx.canvas, 0, 0, w, h);

  // Composite: replace visible (white) pixels with red overlay
  overlayCtx.globalCompositeOperation = 'source-in';
  overlayCtx.fillStyle = MASK_OVERLAY_COLOR;
  overlayCtx.fillRect(0, 0, w, h);

  overlayCtx.restore();
}

/**
 * Export the binary mask canvas as a PNG data URL in the format the OpenAI
 * /v1/images/edits endpoint expects: a PNG with an alpha channel where
 * transparent pixels mark the edit area and opaque pixels mark the region
 * to preserve.
 *
 * The internal mask canvas stores user strokes as semi-transparent paint
 * (alpha > 0 wherever the user drew). We invert that here so painted pixels
 * become fully transparent (alpha 0) and unpainted pixels become opaque
 * white. Without this inversion the entire mask is opaque and OpenAI treats
 * it as "preserve everything" — the model then edits an area of its own
 * choosing, which manifests as the inpainting landing in a different spot
 * than the user painted.
 */
export function exportMaskAsPng(maskCtx: CanvasRenderingContext2D): string {
  const dpr = getDevicePixelRatio();
  const logicalW = Math.round(maskCtx.canvas.width / dpr);
  const logicalH = Math.round(maskCtx.canvas.height / dpr);

  // Create offscreen canvas at logical (1:1) dimensions
  const offscreen = document.createElement('canvas');
  offscreen.width = logicalW;
  offscreen.height = logicalH;
  const offCtx = offscreen.getContext('2d')!;

  // Draw the HiDPI mask onto the offscreen canvas at 1:1 scale
  offCtx.drawImage(maskCtx.canvas, 0, 0, logicalW, logicalH);

  // Invert alpha: painted pixels → transparent (edit), unpainted → opaque
  // white (preserve). The loop bound with step 4 keeps every [i, i+3]
  // index in range, so the non-null assertions hold under
  // `noUncheckedIndexedAccess`.
  const imageData = offCtx.getImageData(0, 0, logicalW, logicalH);
  const pixels = imageData.data;
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3]! > 0) {
      // User painted here — mark for edit (transparent)
      pixels[i] = 0;
      pixels[i + 1] = 0;
      pixels[i + 2] = 0;
      pixels[i + 3] = 0;
    } else {
      // Untouched — preserve (opaque white)
      pixels[i] = 255;
      pixels[i + 1] = 255;
      pixels[i + 2] = 255;
      pixels[i + 3] = 255;
    }
  }
  offCtx.putImageData(imageData, 0, 0);

  return offscreen.toDataURL('image/png');
}

function getDevicePixelRatio(): number {
  return typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
}

/**
 * Set up a canvas for HiDPI rendering.
 * Sets internal dimensions to logicalWidth * dpr, CSS dimensions to logical size.
 */
export function setupHiDPICanvas(
  canvas: HTMLCanvasElement,
  logicalWidth: number,
  logicalHeight: number,
): CanvasRenderingContext2D | null {
  const dpr = getDevicePixelRatio();
  canvas.width = logicalWidth * dpr;
  canvas.height = logicalHeight * dpr;
  canvas.style.width = `${logicalWidth}px`;
  canvas.style.height = `${logicalHeight}px`;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.scale(dpr, dpr);
  }
  return ctx;
}

/**
 * Set up the mask canvas with willReadFrequently for future undo/export reads.
 */
export function setupMaskCanvas(
  canvas: HTMLCanvasElement,
  logicalWidth: number,
  logicalHeight: number,
): CanvasRenderingContext2D | null {
  const dpr = getDevicePixelRatio();
  canvas.width = logicalWidth * dpr;
  canvas.height = logicalHeight * dpr;
  canvas.style.width = `${logicalWidth}px`;
  canvas.style.height = `${logicalHeight}px`;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx) {
    ctx.scale(dpr, dpr);
  }
  return ctx;
}
